/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Section, Header, FloTable, FloSearchBox, FloButton } from 'components';
import Content from 'containers/common/Content';
import { Col } from 'react-bootstrap';
import styles from './Users.module.css';
import { useTranslation } from 'react-i18next';
import NewUserModal from './NewUser';
import EditUserNameModal from './EditUserName';
import { User, UserInviteStatus } from 'model';
import { ListView } from 'model/state';
import ConfirmationDialog from 'containers/common/ConfirmationDialog';
import ToastMessage from 'containers/common/ToastMessage';
import EditUserRoleModal from './EditUserRole';
import { isAccountFullAccess, canUpdateAccount, canRemoveAccount, canCreateAccount, PermissionsEnum, getAccountRoleKeysFromPermissions } from 'model/Role';
import { useHistory } from 'react-router-dom';
import { FloTableActions } from 'components/FloTable/model';
import { useConfirmationPopupFunction } from 'effects';

type UsersProps = {
  accountId?: string,
  fetchUserList: (accountId: string) => void,
  fetchUser: (userId: string) => void,
  removeUser: (userId: string) => void,
  resendUserInvite: (user?: User) => void,
  revokeUserInvite: (user?: User) => void,
  userList: ListView<User>,
  hasError: boolean,
  isSubmitting: boolean,
  clearMessage: () => void,
  clearCurrentUser: () => void,
  openConfirmationDialog: (metadata?: { [key: string]: any }) => void,
  updateSearch: (search: string) => void,
  search: string,
  loggedUser?: User,
}

export const Users: React.FC<UsersProps> = React.memo(({
  accountId,
  fetchUserList,
  userList,
  fetchUser,
  removeUser,
  resendUserInvite,
  revokeUserInvite,
  hasError,
  isSubmitting,
  clearMessage,
  clearCurrentUser,
  openConfirmationDialog,
  search,
  updateSearch,
  loggedUser
}) => {

  const { t } = useTranslation();
  const history = useHistory();
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [confirmationData, handleOpenConfirmationPopup] = useConfirmationPopupFunction(openConfirmationDialog);

  useEffect(() => {
    if (!isSubmitting && !hasError) {
      setShowEditModal(false);
      setShowNewModal(false);
      setShowEditRoleModal(false);
      if (accountId) {
        fetchUserList(accountId);
      }
    }
  }, [accountId, isSubmitting]);

  const handleNewUserClose = () => {
    setShowNewModal(false);
    clearMessage();
  };

  const handleEditNameClose = () => {
    setShowEditModal(false);
    clearMessage();
    clearCurrentUser();
  };

  const handleEditRoleClose = () => {
    setShowEditRoleModal(false);
    clearMessage();
    clearCurrentUser();
  };


  const table = (userList.rows || [])
    .filter(row => row.id === loggedUser?.id || isAccountFullAccess(loggedUser))
    .map(row => ({
      ...row,
      name: `${row.firstName} ${row.lastName}`,
      role: t(getAccountRoleKeysFromPermissions(row.accountRole?.roles || []) || ''),
      activationStatus: t(row.status || 'active'),
  }));

  const actions: FloTableActions = [{
    id: 'resend-user-invite-action',
    onClick: (userId) => {
      const user = userList.rows?.find(row => row.id === userId);
      resendUserInvite(user);
    },
    text: t('resend-user-invite'),
    disabled: (userId) => {
      if (!canCreateAccount(loggedUser)) {
        return true;
      }
      const user = userList.rows?.find(row => row.id === userId);
      return user ? user.status !== UserInviteStatus.PENDING : false;
    },
    show: (userId) => {
      if (!canCreateAccount(loggedUser)) {
        return false;
      }
      const user = userList.rows?.find(row => row.id === userId);
      return user ? user.status === UserInviteStatus.PENDING : false;
    }
  },
  {
    id: 'revoke-user-invite-action',
    onClick: (userId) => {
      const user = userList.rows?.find(row => row.id === userId);
      revokeUserInvite(user);
    },
    text: t('revoke-user-invite'),
    disabled: (userId) => {
      if (!canCreateAccount(loggedUser)) {
        return true;
      }
      const user = userList.rows?.find(row => row.id === userId);
      return user ? user.status !== UserInviteStatus.PENDING : false;
    },
    show: (userId) => {
      if (!canCreateAccount(loggedUser)) {
        return false;
      }
      const user = userList.rows?.find(row => row.id === userId);
      return user ? user.status === UserInviteStatus.PENDING : false;
    }
  },
  {
      id: 'edit-name-email-action',
      onClick: (key: string) => {
        fetchUser(key);
        setShowEditModal(true)
      },
      text: t('edit-name-email'),
      disabled: (userId) => {
        const user = userList.rows?.find(row => row.id === userId);
        return user?.status === UserInviteStatus.PENDING;
      },
    },
    {
      id: 'edit-location-roles-action',
      onClick: (key: string) => {
        history.push(`/users/${key}/roles`);
      },
      text: t('edit-location-roles-action-title'),
      disabled: (userId) => {
        const user = userList.rows?.find(row => row.id === userId);
        return user?.status === UserInviteStatus.PENDING;
      },
    },
  ];

  if (canUpdateAccount(loggedUser)) {
    actions.push({
      id: 'edit-role-permission-action',
      onClick: (key) => {
        fetchUser(key);
        setShowEditRoleModal(true);
      },
      text: t('edit-role-permission'),
      disabled: (userId) => {
        const user = userList.rows?.find(row => row.id === userId);
        return user ? (user.accountRole?.roles || []).includes(PermissionsEnum.OWNER) || (user.id === loggedUser?.id) || user.status === UserInviteStatus.PENDING : false;
      }
    })
  }

  if (canRemoveAccount(loggedUser)) {
    actions.push({
      id: 'delete-user-action',
      onClick: (key) =>  {
        const user = userList.rows?.find(row => row.id === key);
        handleOpenConfirmationPopup(
          t('confirmation-title'),
          t('confirmation-description', { text: `"${user?.firstName} ${user?.lastName}"` }),
          t('confirmation-accept-action'),
          () => removeUser(key)
        );
      },
      text: t('delete-user'),
      disabled: (userId) => {
        const user = userList.rows?.find(row => row.id === userId);
        return user ? (user.accountRole?.roles || []).includes(PermissionsEnum.OWNER) || (user.id === loggedUser?.id) || user.status === UserInviteStatus.PENDING : false;
      }
    });
  }

  return (
    <Section>
      { !showNewModal && !showEditModal && <ToastMessage />}
      <Header>
        <Col xs="12" sm="8" lg="9" className={styles.headerTitleContainer}>
          <h2 className={styles.title}>{t('Users', { count: isAccountFullAccess(loggedUser) ? userList.total : 1 })}</h2>
          { canCreateAccount(loggedUser) &&
            <FloButton
              className = {styles.addButton}
              text      = {`+ ${t('Add User')}`}
              onClick   = {() => {
                setShowNewModal(true);
                clearMessage();
              }}
            />
          }
        </Col>
        <Col xs="12" sm="4" lg="3">
          <FloSearchBox
            className   = {styles.searchBox}
            value       = {search}
            onChange    = {updateSearch}
            placeholder = {t('Search')}
          />
        </Col>
      </Header>
      <Content>
        <Col xs="12" className="pl-0 pr-0">
          <FloTable
            className = {styles.usersTable}
            columns = {[
              { id: 'id', title: 'Id', isHidden: true, isKey: true },
              { id: 'name', title: t('Name') },
              { id: 'email', title: t('Email') },
              { id: 'activationStatus', title: t('Status') },
              { id: 'role', title: t('Role') },
            ]}
            data = {table}
            actions = {actions}
          />
          <NewUserModal
            show    = {showNewModal}
            onHide  = {handleNewUserClose}
          />
          <EditUserNameModal
            show    = {showEditModal}
            onHide  = {handleEditNameClose}
          />
          <EditUserRoleModal
            show    = {showEditRoleModal}
            onHide  = {handleEditRoleClose}
          />
          <ConfirmationDialog
            title       = {confirmationData.title}
            description = {confirmationData.description}
            actionText  = {confirmationData.actionText}
            onAccept    = {confirmationData.onAccept}
          />
        </Col>
      </Content>
    </Section>
  );
});

export default Users;
